import React from "react";
import { computeColorFromIndex } from "../../../../utils/gradient";

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import {
  LineChart,
} from 'echarts/charts'
import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  TransformComponent,
  DatasetComponent,
} from 'echarts/components'
import {
  CanvasRenderer,
} from 'echarts/renderers'

import { useAuth } from '@graniot/common/context/AuthContext'
import { useParcelsLayersStatisticsDate } from "../../../../adapters/parcels";
import { usePaletteScript } from "../../../../adapters/paletteRange";
import { usePaletteAdjustmentsEnabled } from "../../../../context/PaletteAdjustmentsContext";
import { rescaleLegend } from "../../../../utils/palette";

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LineChart,
    CanvasRenderer,
    DatasetComponent,
    ToolboxComponent,
    LegendComponent,
    DataZoomInsideComponent,
    DataZoomSliderComponent,
    TransformComponent,
  ])

  function convertBinsToData(jsonData) {
    const histogram = jsonData.data[0]?.histogram;
    if (!histogram || !histogram[4]?.bins) return [];
    const histogramBins = histogram[4].bins;

    return histogramBins.map((bin, index) => [
        Number(parseFloat(bin.lowEdge).toFixed(2)),
        Number(parseFloat(bin.highEdge).toFixed(2)),
        bin.area_ha,
        `Bin ${index + 1}`,
        bin.percentage,
    ]);
}
export const BasicStatsParcel = ({ layer, parcel, stats }) => {
    const { user } = useAuth()
    const eChartsRef = React.useRef(null)
    const {
        isLoading,
        data: dataStats,
        refetch,
      } = useParcelsLayersStatisticsDate(
        user,
        parcel,
        layer,
        stats.date
    )

    // Colour the histogram with the user's saved palette range for this farm/layer, like the
    // maps above it (only in apps that enable palette adjustments)
    const paletteEnabled = usePaletteAdjustmentsEnabled()
    const { data: palette } = usePaletteScript(
        { parcelId: parcel?.id, layerKey: layer?.key, rangesOnly: true },
        { enabled: paletteEnabled }
    )
    const hasCustomRange = palette?.min != null && palette?.max != null
    const legend = React.useMemo(
        () => (hasCustomRange ? rescaleLegend(layer.legend, palette) : layer.legend),
        [layer, hasCustomRange, palette?.min, palette?.max]
    )

    if(!dataStats || !(dataStats?.data?.length > 0) || isLoading) return null;

    const binsData = convertBinsToData(dataStats);
    if (binsData.length === 0) return null;

    const data = binsData.map(function (item, index) {
      return {
          value: item,
          itemStyle: {
          // Bin midpoint: the lower edge misrepresents wide bins when the palette is stretched
          color: computeColorFromIndex((item[0] + item[1]) / 2, legend)
          }
      };
    });

    // Extract and format min, max, and mean
    const { min, max, mean } = dataStats.data[0]?.basicStats || {};
    if (min == null || max == null || mean == null) return null;
    const minRounded = parseFloat(parseFloat(min).toFixed(2));
    const maxRounded = parseFloat(parseFloat(max).toFixed(2));
    const meanRounded = parseFloat(parseFloat(mean).toFixed(2));
    return (
        <div style={{ height: 240, width: '100%' }}>
            <ReactEChartsCore
                key={`${layer.id}-${stats.date}-${hasCustomRange ? `${palette.min}-${palette.max}` : 'default'}`}
                ref={eChartsRef}
                echarts={echarts}
                style={{ height: 235 }}
                option={{
                    tooltip: {},
                    xAxis: {
                      scale: true
                    },
                    yAxis: {
                        axisLabel: { show: false },
                        axisTick: { show: false },
                        axisLine: { show: false },
                        splitLine: { show: false }
                    },
                    series: [
                      {
                        type: 'custom',
                        renderItem: function (params, api) {
                          var yValue = api.value(2);
                          var start = api.coord([api.value(0), yValue]);
                          var size = api.size([api.value(1) - api.value(0), yValue]);
                          var style = api.style();
                          return {
                            type: 'rect',
                            shape: {
                              x: start[0],
                              y: start[1],
                              width: size[0],
                              height: size[1]
                            },
                            style: style
                          };
                        },
                        label: {
                            show: true,
                            position: "inside", // Place label inside the column
                            formatter: function (params) {
                                const areaHa = params.value[2]; // Access area_ha value
                                return `${areaHa} ha`; // Format as "X ha"
                            },
                            fontSize: 12,
                            color: "#000", // Black label for better contrast
                        },
                        dimensions: ['From', 'To', 'Area (ha)', 'Bin', 'Percentage (%)'],
                        encode: {
                          x: [0, 1],
                          y: 2,
                          tooltip: [0, 1, 2, 4]
                        },
                        data: data,
                        markLine: {
                            tooltip: { show: false }, // Disable tooltips on markLine
                            lineStyle: { width: 3 }, // Make lines thicker
                            data: [
                                {
                                    xAxis: minRounded,
                                    name: minRounded,
                                    lineStyle: { color: computeColorFromIndex(min, legend) }, // Specific color for Min
                                },
                                {
                                    xAxis: meanRounded,
                                    name: meanRounded,
                                    lineStyle: { color: computeColorFromIndex(mean, legend) }, // Specific color for Mean
                                },
                                {
                                    xAxis: maxRounded,
                                    name: maxRounded,
                                    lineStyle: { color: computeColorFromIndex(max, legend) }, // Specific color for Max
                                },
                            ],
                            label: {
                                formatter: "{b}",
                            },
                            symbol: "none", // Removes arrow symbols
                        },
                      }
                    ]
                  }}
            />     
        </div>
    )
}
